import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone, ArrowRight, CheckCircle, Clock, AlertTriangle, Zap, Shield
} from 'lucide-react';
import { companyInfo } from '../../data/mock';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import SEO from '../seo/SEO';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../ui/breadcrumb';

const VitrerieUrgencePage = () => {
  const pageTitle = "Vitrier Urgence Villiers-sur-Marne | 24h/24 7j/7";
  const pageDescription = "Vitrier urgence Villiers-sur-Marne ☎ 07 81 55 90 50. Dépannage vitrerie 24h/24. Vitre cassée, bris de glace, intervention immédiate en moins d'1 heure. Artisan vitrier disponible jour et nuit.";
  const pageKeywords = "vitrier urgence Villiers-sur-Marne, vitrier 24h/24 Villiers-sur-Marne, dépannage vitrerie urgence, vitrier nuit, vitre cassée urgence, bris de glace urgence 94350";

  return (
    <div className="min-h-screen">
      <SEO 
        title={pageTitle}
        description={pageDescription}
        keywords={pageKeywords}
        canonical="/vitrier-urgence-villiers-sur-marne"
      />

      {/* Hero Urgence */}
      <section className="relative py-20 bg-[hsl(24,95%,53%)]">
        <div className="container mx-auto px-4">
          <Breadcrumb className="mb-6">
            <BreadcrumbList className="text-white/70">
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/" className="hover:text-white">Accueil</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="text-white/50" />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/vitrier-villiers-sur-marne" className="hover:text-white">Vitrier Villiers-sur-Marne</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="text-white/50" />
              <BreadcrumbItem>
                <BreadcrumbPage className="text-white">Urgence 24h/24</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="max-w-4xl">
            <div className="flex items-center gap-4 mb-6">
              <AlertTriangle className="w-16 h-16 text-white" />
              <Badge className="bg-white text-[hsl(24,95%,53%)] text-lg px-4 py-2">
                URGENCE 24h/24
              </Badge>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Vitrier Urgence <span className="block">Villiers-sur-Marne</span>
            </h1>
            
            <p className="text-xl text-white/90 mb-8">
              Dépannage vitrerie 24h/24 et 7j/7 à Villiers-sur-Marne. 
              Vitre cassée, bris de glace : notre vitrier intervient en moins d'1 heure.
            </p>

            <a href={`tel:${companyInfo.phoneEmergency}`}>
              <Button size="lg" className="bg-white text-[hsl(24,95%,53%)] hover:bg-gray-100 rounded-full text-2xl px-12 py-8 font-bold shadow-2xl">
                <Phone className="w-8 h-8 mr-3" />
                {companyInfo.phoneEmergency}
              </Button>
            </a>

            <div className="flex flex-wrap items-center gap-6 mt-10 text-white">
              <div className="flex items-center gap-2">
                <Zap className="w-6 h-6" />
                <span className="font-semibold">Intervention -1h</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-6 h-6" />
                <span className="font-semibold">24h/24 - 7j/7</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-6 h-6" />
                <span className="font-semibold">Sécurisation immédiate</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Situations d'urgence */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-[hsl(215,25%,15%)] mb-8 text-center">
            Situations d'urgence vitrerie à Villiers-sur-Marne
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Vitre cassée cambriolage",
                desc: "Sécurisation et remplacement immédiat après effraction",
                urgent: true
              },
              {
                title: "Bris de glace accidentel",
                desc: "Intervention rapide pour bris de glace fenêtre ou baie vitrée",
                urgent: true
              },
              {
                title: "Vitrine magasin cassée",
                desc: "Réparation urgente pour les professionnels",
                urgent: true
              },
              {
                title: "Double vitrage fissuré",
                desc: "Remplacement double vitrage endommagé",
                urgent: false
              },
              {
                title: "Porte vitrée cassée",
                desc: "Remplacement vitrage porte d'entrée ou intérieure",
                urgent: true
              },
              {
                title: "Véranda ou vérandière",
                desc: "Réparation vitrage véranda",
                urgent: false
              }
            ].map((item, i) => (
              <Card key={i} className={`border-0 shadow-md ${item.urgent ? 'border-l-4 border-l-[hsl(24,95%,53%)]' : ''}`}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-3">
                    {item.urgent && <AlertTriangle className="w-5 h-5 text-[hsl(24,95%,53%)] mt-1" />}
                    <div>
                      <h3 className="font-bold text-[hsl(215,25%,15%)] mb-2">{item.title}</h3>
                      <p className="text-muted-foreground text-sm">{item.desc}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Processus intervention */}
      <section className="py-16 bg-[hsl(210,20%,98%)]">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-[hsl(215,25%,15%)] mb-8 text-center">
            Notre processus d'intervention urgence
          </h2>
          
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: "1", title: "Appel", desc: "Appelez notre vitrier urgence" },
              { step: "2", title: "Départ", desc: "Départ immédiat du technicien" },
              { step: "3", title: "Arrivée", desc: "Sur place en moins d'1 heure" },
              { step: "4", title: "Réparation", desc: "Remplacement vitre effectué" }
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 rounded-full bg-[hsl(207,78%,42%)] text-white text-2xl font-bold flex items-center justify-center mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="font-bold text-[hsl(215,25%,15%)] mb-2">{item.title}</h3>
                <p className="text-muted-foreground text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contenu SEO */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <h2 className="text-2xl font-bold text-[hsl(215,25%,15%)] mb-6">
              Vitrier urgence 24h/24 à Villiers-sur-Marne
            </h2>
            
            <p className="text-muted-foreground">
              <strong>NGL SERVICES</strong> est votre <strong>vitrier urgence à Villiers-sur-Marne</strong>, 
              disponible <strong>24h/24 et 7j/7</strong>. Notre service de <strong>dépannage vitrerie</strong> 
              intervient rapidement pour toute situation d'urgence : <strong>vitre cassée</strong>, 
              <strong>bris de glace</strong>, effraction, ou tout autre problème nécessitant une intervention immédiate.
            </p>
            
            <p className="text-muted-foreground">
              Que ce soit en pleine nuit, le week-end ou les jours fériés, notre <strong>artisan vitrier</strong> 
              se déplace en urgence à Villiers-sur-Marne (94350) et dans tout le Val-de-Marne. 
              Nous garantissons une arrivée en moins d'une heure pour sécuriser votre habitation 
              et procéder au remplacement de votre vitrage.
            </p>
          </div>
        </div>
      </section>

      {/* Liens internes */}
      <section className="py-12 bg-[hsl(210,20%,98%)]">
        <div className="container mx-auto px-4">
          <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-6">Voir aussi :</h3>
          <div className="flex flex-wrap gap-4">
            <Link to="/vitrier-villiers-sur-marne" className="px-4 py-2 bg-white text-[hsl(207,78%,42%)] rounded-full text-sm shadow hover:shadow-md">
              Vitrier Villiers-sur-Marne
            </Link>
            <Link to="/remplacement-vitre-cassee-villiers-sur-marne" className="px-4 py-2 bg-white text-[hsl(207,78%,42%)] rounded-full text-sm shadow hover:shadow-md">
              Remplacement vitre cassée
            </Link>
            <Link to="/bris-de-glace-villiers-sur-marne" className="px-4 py-2 bg-white text-[hsl(207,78%,42%)] rounded-full text-sm shadow hover:shadow-md">
              Bris de glace
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[hsl(24,95%,53%)]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Urgence vitrerie Villiers-sur-Marne ?
          </h2>
          <p className="text-white/90 mb-8 text-lg">
            Notre vitrier intervient 24h/24 - Appelez maintenant !
          </p>
          <a href={`tel:${companyInfo.phoneEmergency}`}>
            <Button size="lg" className="bg-white text-[hsl(24,95%,53%)] hover:bg-gray-100 rounded-full text-2xl px-12 py-8 font-bold">
              <Phone className="w-8 h-8 mr-3" />
              {companyInfo.phoneEmergency}
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default VitrerieUrgencePage;
